import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Divider,
  Group,
  NavLink,
  Stack,
  Text,
  Title,
  Tooltip,
  Kbd,
  UnstyledButton,
} from '@mantine/core';
import { Spotlight, spotlight } from '@mantine/spotlight';
import { useAppColorScheme } from '../lib/colorScheme';
import { ThemeToggle } from '../components/ThemeToggle';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconCalendarEvent,
  IconDoorExit,
  IconFile,
  IconHanger,
  IconHeadphones,
  IconHome,
  IconMusic,
  IconSettings,
  IconSpeakerphone,
  IconSearch,
  IconUsers,
} from '@tabler/icons-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/useAuth';
import { useInactivityLogout } from '../lib/auth/useInactivityLogout';

function getInitials(email: string | undefined): string {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function DashboardLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [desktopCollapsed, setDesktopCollapsed] = useDisclosure(false);
  const { session, capabilities, signOut } = useAuth();
  const isMobile = useMediaQuery('(max-width: 48em)');

  // Automatischer Logout nach 30 Minuten Inaktivität
  useInactivityLogout();

  const userEmail = session?.user?.email ?? 'eingeloggt';
  const isActive = (path: string) =>
    loc.pathname === path || loc.pathname.startsWith(path + '/');
  const { colorScheme } = useAppColorScheme();

  const roleBadgeColor = capabilities.isAdmin
    ? 'brand'
    : capabilities.canManageEvents
      ? 'teal'
      : capabilities.canManageCostumes
        ? 'blue'
      : 'gray';

  const roleLabel = capabilities.isAdmin
    ? 'Admin'
    : capabilities.canManageEvents
      ? 'Events'
      : capabilities.canManageCostumes
        ? 'Kostueme'
      : 'Read-only';

  const toggleNavbar = () => {
    if (isMobile) {
      toggleMobile();
    } else {
      setDesktopCollapsed.toggle();
    }
  };

  const closeNavbar = () => {
    if (isMobile) {
      closeMobile();
    }
  };

  const actions = [
    { id: 'home', label: 'Home', onClick: () => nav('/'), leftSection: <IconHome size={18} /> },
    ...(capabilities.canManageEvents ? [
      { id: 'calendar', label: 'Kalender', onClick: () => nav('/calendar'), leftSection: <IconCalendarEvent size={18} /> },
    ] : []),
    ...(capabilities.canManageCostumes ? [
      { id: 'costumes', label: 'Kostüme', onClick: () => nav('/costumes'), leftSection: <IconHanger size={18} /> },
    ] : []),
    ...(capabilities.isAdmin ? [
      { id: 'news', label: 'News', onClick: () => nav('/news'), leftSection: <IconSpeakerphone size={18} /> },
      { id: 'forms', label: 'Formulare', onClick: () => nav('/forms'), leftSection: <IconFile size={18} /> },
      { id: 'choir', label: 'Chor Uploads', onClick: () => nav('/choir'), leftSection: <IconMusic size={18} /> },
      { id: 'playbacks', label: 'Playbacks', onClick: () => nav('/playbacks'), leftSection: <IconHeadphones size={18} /> },
      { id: 'members', label: 'Mitglieder', onClick: () => nav('/members'), leftSection: <IconUsers size={18} /> },
      { id: 'config', label: 'Einstellungen', onClick: () => nav('/config'), leftSection: <IconSettings size={18} /> },
    ] : []),
  ];

  return (
    <>
    <Spotlight
      actions={actions}
      nothingFound="Nichts gefunden..."
      highlightQuery
      searchProps={{
        placeholder: 'Suchen oder navigieren...',
        leftSection: <IconSearch size={20} />,
      }}
    />
    <AppShell
      header={{ height: 64 }}
      navbar={{
        width: 240,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: desktopCollapsed },
      }}
      padding="md"
      styles={{
        main: { background: 'transparent' },
        header: {
          background: 'var(--header-background)',
          borderBottom: '1px solid var(--app-border)',
          backdropFilter: 'blur(24px)',
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        },
        navbar: {
          background: 'var(--app-surface-2)',
          borderRight: '1px solid var(--app-border)',
          backdropFilter: 'blur(24px)',
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger
              opened={isMobile ? mobileOpened : !desktopCollapsed}
              onClick={toggleNavbar}
              size="sm"
              aria-label="Menüleiste ein- oder ausklappen"
            />
            <img
              src={colorScheme === 'dark' ? '/brand/mk_logo_white.png' : '/brand/mk_logo_black.png'}
              alt="Musical!Kultur"
              style={{ height: 22, width: 'auto', opacity: 0.95 }}
            />
            <Title order={3} c="var(--color-text)" style={{ letterSpacing: '-0.01em' }}>
              Musical!Kultur Admin
            </Title>
          </Group>

          <Group gap="xs">
            <UnstyledButton
              visibleFrom="sm"
              onClick={spotlight.open}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: '8px',
                background: 'var(--app-surface-3)',
                border: '1px solid var(--app-border)',
                color: 'var(--color-text-muted)',
                transition: 'border-color 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--app-border)'}
            >
              <IconSearch size={16} />
              <Text size="sm">Suchen...</Text>
              <Kbd size="xs">⌘ K</Kbd>
            </UnstyledButton>

            <ThemeToggle />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={0}>
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '12px' }}>
          {/* Nav Links — scrollbar wenn nötig */}
          <Stack gap={2} style={{ flex: 1, overflowY: 'auto' }}>
            <NavLink
              label="Home"
              leftSection={<IconHome size={18} />}
              color="brand"
              active={isActive('/')}
              onClick={() => { nav('/'); closeNavbar(); }}
              styles={{ label: { fontWeight: 600 } }}
            />

            {capabilities.canManageEvents && (
              <>
                <Divider
                  my={6}
                  label={
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                      Events
                    </Text>
                  }
                />
                <NavLink
                  label="Kalender"
                  leftSection={<IconCalendarEvent size={18} />}
                  color="brand"
                  active={isActive('/calendar')}
                  onClick={() => { nav('/calendar'); closeNavbar(); }}
                />
              </>
            )}

            {capabilities.canManageCostumes && (
              <>
                <Divider
                  my={6}
                  label={
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                      Kostüme
                    </Text>
                  }
                />
                <NavLink
                  label="Kostüme"
                  leftSection={<IconHanger size={18} />}
                  color="brand"
                  active={isActive('/costumes')}
                  onClick={() => { nav('/costumes'); closeNavbar(); }}
                />
              </>
            )}

            {capabilities.isAdmin && (
              <>
                <Divider
                  my={6}
                  label={
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                      Kommunikation
                    </Text>
                  }
                />
                <NavLink
                  label="News"
                  leftSection={<IconSpeakerphone size={18} />}
                  color="brand"
                  active={isActive('/news')}
                  onClick={() => { nav('/news'); closeNavbar(); }}
                />
                <NavLink
                  label="Formulare"
                  leftSection={<IconFile size={18} />}
                  color="brand"
                  active={isActive('/forms')}
                  onClick={() => { nav('/forms'); closeNavbar(); }}
                />

                <Divider
                  my={6}
                  label={
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                      Medien
                    </Text>
                  }
                />
                <NavLink
                  label="Chor Uploads"
                  leftSection={<IconMusic size={18} />}
                  color="brand"
                  active={isActive('/choir')}
                  onClick={() => { nav('/choir'); closeNavbar(); }}
                />
                <NavLink
                  label="Playbacks"
                  leftSection={<IconHeadphones size={18} />}
                  color="brand"
                  active={isActive('/playbacks')}
                  onClick={() => { nav('/playbacks'); closeNavbar(); }}
                />

                <Divider
                  my={6}
                  label={
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                      Verwaltung
                    </Text>
                  }
                />
                <NavLink
                  label="Mitglieder"
                  leftSection={<IconUsers size={18} />}
                  color="brand"
                  active={isActive('/members')}
                  onClick={() => { nav('/members'); closeNavbar(); }}
                />
                <NavLink
                  label="Einstellungen"
                  leftSection={<IconSettings size={18} />}
                  color="brand"
                  active={isActive('/config')}
                  onClick={() => { nav('/config'); closeNavbar(); }}
                />
              </>
            )}
          </Stack>

          {/* User-Sektion am Sidebar-Bottom */}
          <Box
            style={{
              borderTop: '1px solid var(--app-border)',
              paddingTop: 10,
              marginTop: 8,
            }}
          >
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
                <Avatar
                  size={30}
                  radius="xl"
                  color="brand"
                  variant="filled"
                  style={{ fontWeight: 700, fontSize: 12, flexShrink: 0, cursor: 'default' }}
                >
                  {getInitials(session?.user?.email)}
                </Avatar>
                <Box style={{ overflow: 'hidden', flex: 1 }}>
                  <Text
                    size="xs"
                    fw={600}
                    c="var(--color-text)"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {userEmail}
                  </Text>
                  <Badge color={roleBadgeColor} variant="light" size="xs" mt={1}>
                    {roleLabel}
                  </Badge>
                </Box>
              </Group>
              <Tooltip label="Abmelden" withArrow position="top">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  className="contrast-icon-btn"
                  onClick={async () => {
                    await signOut();
                    nav('/login');
                  }}
                >
                  <IconDoorExit size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        </Box>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box className="app-fade-in">
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
    </>
  );
}
